import { FormEvent, useEffect, useState } from 'react'
import { SplitAuthLayout } from '@/components/auth/SplitAuthLayout'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { authApi } from '@/lib/api/auth.api'
import { createLogger } from '@/lib/logger'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'

const logger = createLogger('pages/reset-password')

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase()
}

export default function ResetPasswordPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const emailParam = searchParams.get('email')
    if (emailParam) {
      setEmail(normalizeEmail(emailParam))
    }
  }, [searchParams])

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    const normalizedEmail = normalizeEmail(email)
    const normalizedCode = code.trim()

    if (!normalizedEmail || !normalizedCode || !newPassword) {
      setError('Email, reset code, and new password are required')
      return
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    try {
      setIsLoading(true)
      setError(null)
      await authApi.resetPassword(normalizedEmail, normalizedCode, newPassword)
      toast.success('Password reset successfully')
      navigate(`/login?email=${encodeURIComponent(normalizedEmail)}`)
    } catch (submitError) {
      logger.error('[ResetPassword] Reset failed:', submitError)
      setError(
        submitError instanceof Error
          ? submitError.message
          : 'Unable to reset password',
      )
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <SplitAuthLayout pageType="reset-password">
      <div className="w-full">
        {error && (
          <Alert
            variant="destructive"
            className="mb-6 border-destructive/30 bg-destructive/10 text-destructive"
          >
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label
              htmlFor="email"
              className="text-sm font-medium text-foreground"
            >
              Email
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={event => setEmail(normalizeEmail(event.target.value))}
              className="h-11 border-input bg-background"
              disabled={isLoading}
              required
            />
          </div>

          <div className="space-y-2">
            <Label
              htmlFor="code"
              className="text-sm font-medium text-foreground"
            >
              Reset Code
            </Label>
            <Input
              id="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="Enter the code from your email"
              value={code}
              onChange={event =>
                setCode(event.target.value.replace(/\D/g, '').slice(0, 6))
              }
              className="h-11 border-input bg-background"
              disabled={isLoading}
              required
            />
          </div>

          <div className="space-y-2">
            <Label
              htmlFor="new-password"
              className="text-sm font-medium text-foreground"
            >
              New Password
            </Label>
            <Input
              id="new-password"
              type="password"
              autoComplete="new-password"
              placeholder="Create a new password"
              value={newPassword}
              onChange={event => setNewPassword(event.target.value)}
              className="h-11 border-input bg-background"
              disabled={isLoading}
              required
            />
          </div>

          <div className="space-y-2">
            <Label
              htmlFor="confirm-password"
              className="text-sm font-medium text-foreground"
            >
              Confirm Password
            </Label>
            <Input
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              placeholder="Confirm your new password"
              value={confirmPassword}
              onChange={event => setConfirmPassword(event.target.value)}
              className="h-11 border-input bg-background"
              disabled={isLoading}
              required
            />
          </div>

          <Button
            type="submit"
            className="h-11 w-full font-medium"
            disabled={isLoading}
          >
            {isLoading ? 'Resetting password...' : 'Reset Password'}
          </Button>
        </form>

        <div className="mt-8 flex items-center justify-center gap-4 text-sm">
          <Link
            to={
              email
                ? `/forgot-password?email=${encodeURIComponent(normalizeEmail(email))}`
                : '/forgot-password'
            }
            className="font-medium text-primary hover:text-primary/80"
          >
            Request a new code
          </Link>
          <span className="text-muted-foreground">/</span>
          <Link
            to={
              email
                ? `/login?email=${encodeURIComponent(normalizeEmail(email))}`
                : '/login'
            }
            className="font-medium text-primary hover:text-primary/80"
          >
            Back to sign in
          </Link>
        </div>
      </div>
    </SplitAuthLayout>
  )
}
